import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AdminBrandHeader,
  AdminNavList,
  AdminBackToStoreLink,
  resolveAdminPageTitle,
} from './AdminSidebar';

/**
 * Mobile-only (<md) sticky top bar for the admin shell.
 * - Hamburger button opens a left-side Sheet that renders the same nav as the
 *   desktop sidebar.
 * - Page title (left, font-display) is derived from the current route.
 * - Right slot reserved for an optional page-specific primary CTA (wired in
 *   later phases via context; rendered as a no-op for now).
 *
 * On ≥md this component renders nothing (`md:hidden` on the outer element),
 * preserving the desktop layout byte-for-byte.
 */
export default function AdminMobileTopBar({ rightSlot = null }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const title = resolveAdminPageTitle(location.pathname);

  // Drawer is closed explicitly by each nav link via `onNavigate={closeDrawer}`
  // and by Radix Sheet's overlay-tap / Esc / drag dismiss. No route-driven
  // effect is needed.
  const closeDrawer = () => setOpen(false);

  return (
    <header
      className="md:hidden sticky top-0 z-40 h-14 bg-sunk border-b border-border flex items-center px-2 gap-2"
      data-testid="admin-mobile-topbar"
    >
      <Sheet open={open} onOpenChange={setOpen}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open admin menu"
          aria-expanded={open}
          aria-controls="admin-mobile-drawer"
          data-testid="admin-mobile-menu-trigger"
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
        >
          <Menu className="h-6 w-6" />
        </button>

        <SheetContent
          side="left"
          id="admin-mobile-drawer"
          data-testid="admin-mobile-drawer"
          className="p-0 w-72 max-w-[85vw] bg-sunk border-r border-border flex flex-col"
        >
          <SheetTitle className="sr-only">Admin navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Primary navigation for the Batchtrue Wholesale admin panel.
          </SheetDescription>
          <AdminBrandHeader />
          <AdminBackToStoreLink onNavigate={closeDrawer} />
          <AdminNavList onNavigate={closeDrawer} idPrefix="admin-mobile-nav" />
        </SheetContent>
      </Sheet>

      <h1
        className="flex-1 font-display font-semibold text-base text-foreground truncate"
        data-testid="admin-mobile-page-title"
      >
        {title}
      </h1>

      <div
        className="flex items-center justify-end min-h-[44px]"
        data-testid="admin-mobile-cta-slot"
      >
        {rightSlot}
      </div>
    </header>
  );
}
